/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from 'antd/components/modal/Modal.tsx'.
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import classNames from 'classnames';
import Dialog from 'rc-dialog';
import { ConfigContext } from '../config-provider';
import LocaleReceiver from 'antd/es/locale-provider/LocaleReceiver';
import { getConfirmLocale } from './locale';
import { canUseDocElement } from 'antd/es/_util/styleChecker';
import { getTransitionName } from 'antd/es/_util/motion';
import { Icon } from '../icon';
import { Button } from '../button';
import { getMaskStyle } from '../_util/maskStyle';
let mousePosition;
// ref: https://github.com/ant-design/ant-design/issues/15795
const getClickPosition = (e) => {
    mousePosition = {
        x: e.pageX,
        y: e.pageY
    };
    // 100ms 内发生过点击事件，则从点击位置动画展示
    // 否则直接 zoom 展示
    // 这样可以兼容非点击方式展开
    setTimeout(() => {
        mousePosition = null;
    }, 100);
};
// 只有点击事件支持从鼠标位置动画展开
if (canUseDocElement()) {
    document.documentElement.addEventListener('click', getClickPosition, true);
}
const Modal = (props) => {
    const { getPopupContainer: getContextPopupContainer, getPrefixCls, direction, animation } = React.useContext(ConfigContext);
    const handleCancel = (e) => {
        const { onCancel } = props;
        onCancel?.(e);
    };
    const handleOk = (e) => {
        const { onOk } = props;
        onOk?.(e);
    };
    const renderFooter = (locale) => {
        const { okText, okType, cancelText, confirmLoading, size } = props;
        const buttonSize = size === 'xs' ? 'small' : 'middle';
        return (React.createElement(React.Fragment, null,
            React.createElement(Button, { onClick: handleCancel, size: buttonSize, ...props.cancelButtonProps, type: 'default' }, cancelText || locale.cancelText),
            React.createElement(Button, { loading: confirmLoading, onClick: handleOk, size: buttonSize, ...props.okButtonProps, type: okType }, okText || locale.okText)));
    };
    const { prefixCls: customizePrefixCls, footer, visible, wrapClassName, centered, getContainer, closeIcon, size, focusTriggerAfterClose = true, width, ...restProps } = props;
    const prefixCls = getPrefixCls('modal', customizePrefixCls);
    const rootPrefixCls = getPrefixCls();
    let sizeWidth;
    switch (size) {
        case 'xs':
            sizeWidth = 340;
            break;
        case 'small':
            sizeWidth = 480;
            break;
        case 'middle':
            sizeWidth = 640;
            break;
        case 'large':
            sizeWidth = 800;
            break;
        case 'fullscreen':
            sizeWidth = '90%';
            break;
    }
    const newWidth = !width ? sizeWidth : width;
    const defaultFooter = (
    // @ts-ignore
    React.createElement(LocaleReceiver, { componentName: 'Modal', defaultLocale: getConfirmLocale() }, renderFooter));
    const closeIconToRender = (React.createElement("span", { className: `${prefixCls}-close-x` }, closeIcon || React.createElement(Icon, { type: 'close', className: `${prefixCls}-close-icon`, size: 'large' })));
    const wrapClassNameExtended = classNames(wrapClassName, {
        [`${prefixCls}-centered`]: !!centered,
        [`${prefixCls}-wrap-rtl`]: direction === 'rtl',
        [`${prefixCls}-xs`]: size === 'xs',
        [`${prefixCls}-sm`]: size === 'small',
        [`${prefixCls}-md`]: size === 'middle',
        [`${prefixCls}-lg`]: size === 'large',
        [`${prefixCls}-fullscreen`]: size === 'fullscreen',
        [`${prefixCls}-nofooter`]: footer === null
    });
    return (React.createElement(Dialog, { maskStyle: getMaskStyle(animation), ...restProps, width: newWidth, getContainer: getContainer === undefined ? getContextPopupContainer : getContainer, prefixCls: prefixCls, wrapClassName: wrapClassNameExtended, footer: footer === undefined ? defaultFooter : footer, visible: visible, mousePosition: mousePosition, 
        // @ts-ignore
        onClose: handleCancel, closeIcon: closeIconToRender, focusTriggerAfterClose: focusTriggerAfterClose, transitionName: getTransitionName(rootPrefixCls, 'zoom', props.transitionName), maskTransitionName: getTransitionName(rootPrefixCls, 'fade', props.maskTransitionName) }));
};
Modal.defaultProps = {
    confirmLoading: false,
    visible: false,
    size: 'middle',
    okType: 'primary',
    cancelText: '取消',
    okText: '确定'
};
// export default withConfigProvider(Modal);
export default Modal;
